import { salesTargetsLink } from '@/lib/targets/months';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Calculator } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { formatINRCompact } from './_dashboardShared';
import { fetchTeamSales, type TeamSalesData } from './salesDashboardData';
import { TeamSalesSections } from './TeamSalesSections';

type Result = { identity: string; data?: TeamSalesData; error?: boolean };

export function SalesDashboard() {
  const user = useAuthStore(state => state.user);
  const identity = `${user?.tenantId}:${user?.id}`;
  const [result, setResult] = useState<Result>();
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setResult(undefined);
    fetchTeamSales().then(data => {
      if (active) setResult({ identity, data });
    }).catch(() => {
      if (active) setResult({ identity, error: true });
    });
    return () => { active = false; };
  }, [identity, attempt]);
  const current = result?.identity === identity ? result : undefined;
  const data = current?.data;
  const cards = data ? [
    { label: 'Team pipeline', value: formatINRCompact(data.pipeline.value), to: '/pipeline',
      detail: `${data.pipeline.count} open leads${data.pipeline.unvalued ? ` · ${data.pipeline.unvalued} without a value` : ''}` },
    { label: 'Won this month', value: data.won.value === null ? 'Value needs review' : formatINRCompact(data.won.value), to: salesTargetsLink(), // 🔒 2026-10-13 — count = closures + paid dealer orders (the target rule).
      detail: `${data.won.count} paid sale${data.won.count === 1 ? '' : 's'} · incl. dealer · ${data.period}` },
    { label: 'Conversion', value: data.conversion === null ? 'No leads in period' : `${data.conversion.toFixed(1)}%`, to: '/scorecard/team', detail: 'Lead to won · leads created in the last 90 days' },
    { label: 'Overdue follow-ups', value: String(data.overdue.length), to: '/admin/follow-ups', detail: `${data.overdue.filter(row => Date.now() - Date.parse(row.due) > 3 * 86_400_000).length} older than 3 days` },
    { label: 'Waiting for you', value: String(data.approvals.length), to: '/discount-requests', detail: 'Discount requests with your team' },
  ] : [];

  return <div className="aura-root min-h-full p-4 md:p-6" style={{ background: 'var(--ivory-0)', color: 'var(--ink-0)' }}>
    <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
      <div>
        <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 26, fontWeight: 500 }}>Team Sales</h2>
        <p className="text-sm text-stone-500">My team{data ? ` · ${data.period}` : ''}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Link className="btn btn-outline inline-flex items-center gap-2" to="/leads"><Plus size={15} />New lead</Link>
        <Link className="btn inline-flex items-center gap-2" to="/quick-quote"><Calculator size={15} />Quick Quote</Link>
      </div>
    </header>
    {!current && <p role="status" className="card p-6">Loading Team Sales…</p>}
    {current?.error && <div role="alert" className="card border border-red-200 p-6">
      <p>Could not load Team Sales. Retry to load your team's figures.</p>
      <button className="btn mt-3" onClick={() => setAttempt(value => value + 1)}>Retry</button>
    </div>}
    {data && <>
      <div aria-label="Team sales KPIs" className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map(card => <Link key={card.label} to={card.to} className="card min-w-0 p-4">
          <div className="kpi-label text-xs">{card.label}</div>
          <div className="num-display my-2 break-words text-2xl">{card.value}</div>
          <p className="text-xs text-stone-500">{card.detail}</p>
        </Link>)}
      </div>
      <TeamSalesSections data={data} />
    </>}
  </div>;
}
